import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { FiMessageSquare } from "react-icons/fi";
import Sidebar from "../components/userDashboard/Sidebar";
import UserMobileDock from "../components/mobile/UserMobileDock";
import { UserDropdown } from "../components/userDashboard/Topbar";
import { useCheckIn } from "../context/CheckInContext";
import { getAuth, logout } from "../utils/auth";
import { userAPI } from "../services/api";

const UserDashboardLayout = ({ children }) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const { unreadNotifCount, stats } = useCheckIn();
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile header: real profile photo + account dropdown (same as desktop topbar)
  const [profile, setProfile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const load = () => {
      if (!getAuth().token) return;
      userAPI.getProfile()
        .then(res => { if (res.data?.success) { setProfile(res.data.data); setImgFailed(false); } })
        .catch(() => {});
    };
    load();
    window.addEventListener("profile-updated", load);
    return () => window.removeEventListener("profile-updated", load);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [menuOpen]);

  const avatarUrl = profile?.profileImageUrl || profile?.avatarUrl || auth.user?.profileImageUrl || auth.user?.avatarUrl;

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate("/");
  };

  const getInitials = () => {
    const name = profile?.fullName || auth.user?.name || auth.user?.fullName || auth.user?.userName || auth.user?.email || "Y";
    return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "Y";
  };

  return (
    <div className="min-h-screen bg-[#FFF8EE] relative overflow-hidden">
      {/* Background Glows */}
      <div
        className="fixed top-[-250px] right-[-250px] h-[700px] w-[700px] rounded-full bg-[#F29A0C]/10 blur-[180px] pointer-events-none"
      />
      <div
        className="fixed bottom-[-250px] left-[-250px] h-[600px] w-[600px] rounded-full bg-[#6B8A5A]/10 blur-[180px] pointer-events-none"
      />

      {/* Desktop Left Sidebar (Strictly hidden on mobile, untouched on desktop) */}
      <Sidebar
        expanded={sidebarExpanded}
        setExpanded={setSidebarExpanded}
      />

      {/* Mobile Top Brand Header (Strictly md:hidden) */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-2.5 bg-[#FFF8EE]/90 backdrop-blur-md border-b border-[#EFE3D2]/70 shadow-xs">
        <Link to="/user/dashboard" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl overflow-hidden shadow-xs border border-white">
            <img src="/assets/logo.jpeg" alt="Divyyog" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-sm font-black text-stone-900 leading-none tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
              DIVYYOG
            </h1>
            <p className="text-[9px] font-bold text-amber-800/80 tracking-widest uppercase mt-0.5">
              Foundation
            </p>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <Link
            to="/user/notification"
            className="relative w-8 h-8 rounded-full bg-white border border-[#EFE3D2] flex items-center justify-center text-stone-600 shadow-xs active:scale-95"
            aria-label="Notifications"
          >
            <FiMessageSquare size={16} />
            {unreadNotifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-rose-500 text-white text-[8px] font-black rounded-full flex items-center justify-center">
                {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
              </span>
            )}
          </Link>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-tr from-[#F29A0C] to-[#E97818] text-white text-[11px] font-black flex items-center justify-center shadow-xs active:scale-95 border-0 p-0 cursor-pointer"
              style={{ boxShadow: menuOpen ? "0 0 0 2px #fff, 0 0 0 4px rgba(242,154,12,0.6)" : undefined }}
              aria-label="Account menu"
              aria-expanded={menuOpen}
            >
              {avatarUrl && !imgFailed
                ? <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" onError={() => setImgFailed(true)} />
                : getInitials()}
            </button>
            <AnimatePresence>
              {menuOpen && (
                <UserDropdown
                  auth={auth}
                  profile={profile}
                  stats={stats}
                  onLogout={handleLogout}
                  positionClass="absolute right-0 top-[calc(100%+10px)] w-[min(320px,calc(100vw-24px))] max-h-[calc(100dvh-80px)] overflow-y-auto"
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Main Content Container */}
      <main
        className={`
          relative
          z-10
          min-h-screen
          transition-all
          duration-300
          ease-[cubic-bezier(0.25,1,0.5,1)]
          ml-0
          ${sidebarExpanded ? "md:ml-[295px] lg:ml-[310px]" : "md:ml-[88px] lg:ml-[98px]"}
          px-3 sm:px-6 lg:px-8
          py-2 sm:py-5
          pb-28 md:pb-6
          w-auto
          min-w-0
          box-border
          overflow-x-hidden
        `}
      >
        <div className="w-full min-w-0">
          {children}
        </div>
      </main>

      {/* Creative Mobile Bottom Navigation Dock (Strictly md:hidden) */}
      <UserMobileDock />
    </div>
  );
};

export default UserDashboardLayout;